import { Document } from '@/interfaces/requirement';
import { Delete, InfringementToEdit, InfringementToEditFromBack, Reactivate, Suspend } from '@/interfaces/search/search.interface';
import { StoreState } from '@/store';
import { getCurrentUserData } from '@/store/login/selectors';
import { CacheService } from '@/utils/cacheService/cache.service';
import { parseISOString } from '@/utils/parseISOString';
import { EquipmentRowModified } from '@/views/main/license/extend/form/new-record/new-record.interfaces';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { first, map, mergeMap, take } from 'rxjs/operators';
import { DetailLicenseById } from '../../interfaces/search/search.interface';
import { DocumentWithOnlyDates } from './search.interface';

@Injectable({
  providedIn: 'root',
})
export class SearchService {

  constructor(
    private http: HttpClient,
    private cacheService: CacheService,
    private store: Store<StoreState>,
  ) { }

  onReactivate(id: number) {
    this.cacheService.remove('LicensesByUser');
    const storeLoginSlice$ = getCurrentUserData(this.store);
    return storeLoginSlice$.pipe(
      mergeMap((user) => {
        const params: Reactivate = {
          licenseId: id,
          createdMdmid: user.mdmid,
          actionTypeSearch: 1,
        };
        return this.http.post('/License/UpdateToReactivateOrDeleteByLicense', params);
      }),
    );
  }

  // Eliminar licencia (Eliminada)
  onDeleteLicenseById(id: number) {
    this.cacheService.remove('LicensesByUser');
    const storeLoginSlice$ = getCurrentUserData(this.store);
    return storeLoginSlice$.pipe(
      mergeMap((user) => {
        const params: Delete = {
          licenseId: id,
          createdMdmid: user.mdmid,
          actionTypeSearch: 2,
        };
        return this.http.post('/License/UpdateToReactivateOrDeleteByLicense', params).pipe(take(1));
      }),
    );
  }

  onSuspendLicense(suspensionForm: Suspend) {
    this.cacheService.remove('LicensesByUser');
    return this.http.post('/License/AddSuspensionByLicense', suspensionForm).pipe(take(1));
  }

  // Eliminar Equipo (Elmima zonas y tipos de licencia)
  onDeleteEquipment(licenseId: number, equipmentId: number) {
    this.cacheService.remove('LicensesByUser');
    const storeLoginSlice$ = getCurrentUserData(this.store);
    return storeLoginSlice$.pipe(
      mergeMap((user) => this.http.post(`License/License/${licenseId}/Equipment/${equipmentId}/Delete`, { createdMdmid: user.mdmid }).pipe(first())),
    );

  }

  // Eliminar Zona (Debe tener al menos una)
  onDeleteZone(licenseId: number, equipmentId: number, zoneId: number) {
    this.cacheService.remove('LicensesByUser');
    const storeLoginSlice$ = getCurrentUserData(this.store);
    return storeLoginSlice$.pipe(
      mergeMap((user) => this.http.post(`/License/License/${licenseId}/Equipment/${equipmentId}/Zone/${zoneId}/Delete`, { createdMdmid: user.mdmid }).pipe(first())),
    );
  }

  // Eliminar Tipo Licencia (Debe tener al menos una)
  onDeleteTypeLicense(licenseId: number, equipmentId: number, licenseTypeId: number) {
    this.cacheService.remove('LicensesByUser');
    const storeLoginSlice$ = getCurrentUserData(this.store);
    return storeLoginSlice$.pipe(
      mergeMap((user) => this.http.post(`License/License/${licenseId}/Equipment/${equipmentId}/LicenseType/${licenseTypeId}/Delete`, { createdMdmid: user.mdmid }).pipe(first())),
    );
  }

  getDetailLicense(licenseId: number): Observable<DetailLicenseById[]> {
    return this.http.get<DetailLicenseById>(`/License/Simple/License/${licenseId}`).pipe(map((data) => [data]));
  }

  getInfringementDetail(id: number): Observable<InfringementToEdit> {
    return this.http.get<InfringementToEditFromBack>(`/Infringement/InfringementId/${id}/Detail`).pipe(take(1))
      .pipe(
        map(({ dateTimeInfringement, dateStart, dateEnd, ...rest }) => {
          const out: InfringementToEdit = { ...rest };
          if (dateTimeInfringement) {
            const date = parseISOString(dateTimeInfringement, true);
            // fixed hour by hour zone
            date.setHours(date?.getHours() + (date.getTimezoneOffset() / 60));

            out.dateTimeInfringement = date;
          }
          if (dateStart) {
            out.dateStart = parseISOString(dateStart);
          }
          if (dateEnd) {
            out.dateEnd = parseISOString(dateEnd);
          }

          return out;
        }),
        take(1),
      );
  }

  disableInfringement(infringementId: number) {
    this.cacheService.remove('LicensesByUser');
    return this.http.post(`/Infringement/InfringementId/${infringementId}/Delete`, null);
  }

  onModifyEquipment(licenseId: number, equipmentModified: EquipmentRowModified) {
    this.cacheService.remove('LicensesByUser');
    return this.http.put(`/License/License/${licenseId}/Equipment/${equipmentModified.id}`, equipmentModified);
  }

  onModifyDates(licenseId: number, documentsModified: Document[]) {
    this.cacheService.remove('LicensesByUser');
    const payload: DocumentWithOnlyDates[] = documentsModified
      .map(({ id, dateEmit, dateFrom, dateUntil }) => ({
        id,
        createdAt: dateEmit,
        dateFrom,
        dateTo: dateUntil,
      }));
    return this.http.put(`/License/License/${licenseId}/UpdateDocuments`, payload);
  }

}
